import { useQuery, useMutation, useQueryClient } from 'react-query'
import client, { multipartclient, patchclient } from './api-client'

const queryConfig = {
	staleTime: 1000 * 60 * 60,
	cacheTime: 1000 * 60 * 60,
  }

async function fetchProfile(user) {
	let request_arg = null
	if (!user?.company?.id) request_arg = `?client_id=${user.id}`
	else request_arg = user?.company?.id ? `?company=${user.company.id}` : ''
	const data = await client(`profile/${user.id}${request_arg}`)
	return data
}

export function useProfile(user) {
	return useQuery(
		['profile', user.id, user?.company?.id],
		() => fetchProfile(user),
		{
			enabled: true,
			...queryConfig,
		}
	)
}

async function updateProfile(profile) {
	const data = await multipartclient(
		`profile/${profile.user}/`,
		{ body: profile },
		'PATCH'
	)
	return data
}

export function useMutateProfile(profile) {
	const queryClient = useQueryClient()

	return useMutation((profile) => updateProfile(profile), {
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries(['profile', data.user])
		},
	})
}

async function updateProfileIntro(profile) {
	const data = await patchclient(`profile/${profile.user}/`, {
		body: profile,
	})
	return data
}

export function useMutateProfileIntro(profile) {
	const queryClient = useQueryClient()

	return useMutation((profile) => updateProfileIntro(profile), {
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries(['profile_intro', data.user])
		},
	})
}

// Consent management functions
async function checkConsentStatus(user) {
	const data = await client(`profile/${user.id}/check_consent_status/`)
	return data
}

async function updateConsentStatus(user, consentData) {
	const data = await client(`profile/${user.id}/update_consent_status/`, {
		body: consentData
	})
	return data
}

export function useCheckConsentStatus(user) {
	return useQuery(
		['consent_status', user?.id],
		() => checkConsentStatus(user),
		{
			enabled: !!user,
			...queryConfig,
		}
	)
}

export function useMutateConsentStatus() {
	const queryClient = useQueryClient()

	return useMutation(
		({ user, consentData }) => updateConsentStatus(user, consentData),
		{
			onSuccess: (data, variables) => {
				queryClient.invalidateQueries(['consent_status', variables.user.id])
				queryClient.invalidateQueries(['profile', variables.user.id])
			},
		}
	)
}

// Marketing opt-in/out (newsletter, product news, promotions, webinars). Applied on
// its own endpoint, not through the profile PATCH, so it takes effect immediately and
// also updates the user's HubSpot subscription.
async function updateMarketingConsent(user, marketingConsent) {
	const data = await client(`profile/${user.id}/marketing_consent/`, {
		body: { marketing_consent: marketingConsent },
	})
	return data
}

export function useMutateMarketingConsent() {
	return useMutation(({ user, marketingConsent }) =>
		updateMarketingConsent(user, marketingConsent)
	)
}
